import {DateTime} from 'luxon';

import {getCultureCode, getDateMask, getDateTimeMask, getTimeMask} from '@/use/culture';

export function parseRFQDateTime(value: string | null | undefined): DateTime | null {
  if (!value) {
    return null;
  }

  const locale = getCultureCode();
  const formats = [
    getDateTimeMask('short'),
    getDateMask('short'),
    // Accept values created before culture-aware formatting was introduced.
    'yyyy-LL-dd hh:mm a',
    'yyyy-LL-dd',
  ];

  for (const format of formats) {
    const dateTime = DateTime.fromFormat(value, format, {locale});

    if (dateTime.isValid) {
      return dateTime;
    }
  }

  const isoDateTime = DateTime.fromISO(value, {locale});

  return isoDateTime.isValid ? isoDateTime : null;
}

export function parseRFQTime(value: string | null | undefined): DateTime | null {
  if (!value) {
    return null;
  }

  const locale = getCultureCode();
  const formats = [
    getTimeMask('short'),
    'HH:mm:ss',
    'H:mm:ss',
    'h:mm a',
    'hh:mm a',
  ];

  for (const format of formats) {
    const time = DateTime.fromFormat(value, format, {locale});

    if (time.isValid) {
      return time;
    }
  }

  return null;
}

export function serializeRFQDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const date = parseRFQDateTime(value);

  return date?.isValid ? date.toFormat('yyyy-LL-dd') : null;
}

export function serializeRFQTime(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const time = parseRFQTime(value);

  return time?.isValid ? time.toFormat('HH:mm:ss') : null;
}

export default function formatRFQDateTime(
  dateData: string | null | undefined,
  timeData: string | null | undefined,
): string {
  if (dateData && timeData) {
    const dateTime = DateTime.fromFormat(`${dateData} ${timeData}`, 'yyyy-LL-dd HH:mm:ss', {
      locale: getCultureCode(),
    });

    return dateTime.isValid
      ? dateTime.toFormat(getDateTimeMask('short'))
      : `${dateData} ${timeData}`;
  }

  if (dateData) {
    const date = DateTime.fromFormat(dateData, 'yyyy-LL-dd', {locale: getCultureCode()});

    return date.isValid ? date.toFormat(getDateMask('short')) : dateData;
  }

  if (timeData) {
    const time = parseRFQTime(timeData);

    return time ? time.toFormat(getTimeMask('short')) : timeData;
  }

  return '';
}
